/* eslint-disable react/prop-types */
/* eslint-disable consistent-return */
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import useMediaQuery from '@src/hooks/useMediaQuery';
import useSafeAreaClass from '@src/hooks/useSafeAreaClass';
import Menu from '@src/components/menu';

import SideMenuHeader from '@src/components/SideMenuHeader';
import SideMenuFooter from '@src/components/SideMenuFooter';

import { CollapsedIcon } from '@src/components/common/icons/CollapsedIcon';
import { getThemesClientCall } from '@src/store/actions';
import { getPunchInCall } from '@src/store/actions/punchIn';

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	min-height: calc(var(--vh, 1vh) * 100);
`;

const TopBar = styled.div`
	background-color: #fff;
	color: #333;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 20px;
	height: 52px;
	border-bottom: 1px solid #E9E9E9;
	@media ${props => props.theme.device.mobile} {
		display: none;
	}
`;

const SideMenu = styled(motion.div)`
	position: fixed;
	background-color: #fff;
	width: 230px;
	height: calc(var(--vh, 1vh) * 100 - 52px);
	border-right: 1px solid #E9E9E9;
	z-index: 99999;
	@media ${props => props.theme.device.mobile} {
		display: none !important;
		width: 0px;
	}
`;

const MainContent = styled.div`
	flex-grow: 1;
	background-color: #F5F5F7;
	overflow-y: auto;
	touch-action: pan-y;
	-webkit-overflow-scrolling: touch;
	width: calc(100vw - ${props => props.sideMenuWidth}px);
	margin-left: ${props => props.sideMenuWidth}px;
	transition: margin-left 0.3s ease;
	will-change: margin-left, width;
	height: 100%;
	max-height: calc(var(--vh, 1vh) * 100 - 52px - 53px);
	@media ${props => props.theme.device.mobile} {
		width: 100vw;
		margin-left: 0px;
		max-height: unset;
	}
	@media
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet} {
		margin-left: 20px;
	}
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-grow: 1;
	position: relative;
`;

const Footer = styled.div`
	position: fixed;
	bottom: 0;
	right: 0;
	padding-left: ${props => props.sideMenuWidth}px;
	transition: padding-left 0.3s ease;
	will-change: padding-left;
  width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	height: 53px;
	border-top: 1px solid #E9E9E9;
	background: #FFF;
	color: #333;
	@media ${props => props.theme.device.mobile} {
		display: none;
	}
	@media
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet} {
		padding-left: 20px;
	}
`;

const CollapsedWrapper = styled.div`
	position: absolute;
	right: -14px;
	top: 12px;
	border-radius: 100px;
	border: 1px solid #E9E9E9;
	background: #FFF;
	width: 28px;
	height: 28px;
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	transform: rotate(0deg);
	${props => props.collapsed && `
		transform: rotate(180deg);
	`}

	:hover {
		border-radius: 100px;
		border: 1px solid #E9E9E9;
		background: #3091FD;
		& svg path {
			stroke: #FFF;
		}
	}
	@media ${props => props.theme.device.mobile} {
		display: none;
	}
`;

const OpenSlideMask = styled.div`
	position: absolute;
	width: 100vw;
	height: calc(var(--vh, 1vh) * 100 - 52px);
	top: 0;
	left: 0;
	background: rgba(0, 0, 0, 0.50);
	z-index: 99999;
	@media ${props => props.theme.device.mobile} {
		display: none;
	}
`;

const SideMenuWrapper = ({
	children,
}: {
	children: React.ReactNode;
}): JSX.Element => {
	const { mediaMatch: { isDesktop, isLgDesktop } } = useMediaQuery();
	const detail = useSelector((state: RootStateOrAny) => state.punchIn.punchIn) || {};
	const router = useRouter();
	const dispatch = useDispatch();
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [collapsed, setCollapsed] = useState(false);
	const isSmallerThanDesktop = !isDesktop && !isLgDesktop;
	const sideMenuWidth = collapsed ? 230 : 20;
	useSafeAreaClass();
	useEffect(() => {
		if (isDesktop || isLgDesktop) {
			setCollapsed(true);
		}
	}, [isDesktop, isLgDesktop]);
	useEffect(() => {
		dispatch(getThemesClientCall({ temporary: false, disableLoading: true }));
		dispatch(getPunchInCall());
	}, []);

	return (
		<Wrapper>
			<TopBar>
				<SideMenuHeader />
			</TopBar>
			<ContentWrapper>
				{collapsed && isSmallerThanDesktop && <OpenSlideMask onClick={() => setCollapsed(false)} />}
				<SideMenu
					sideMenuWidth={sideMenuWidth}
					isOpenMenu={collapsed}
					variants={{
						open: { x: 0 },
						closed: { x: '-90%' },
					}}
					animate={collapsed ? 'open' : 'closed'}
					transition={{ ease: 'easeOut' }}
				>
					{
						collapsed && (
							<Menu
								showOutingPunch={detail?.field_punch_enabled}
								collapsed={collapsed}
								onClick={path => {
									if (isSmallerThanDesktop) setCollapsed(false);
									router.push(path);
								}}
							/>
						)
					}
					<CollapsedWrapper
						collapsed={collapsed}
						onClick={() => setCollapsed(!collapsed)}
					>
						<CollapsedIcon />
					</CollapsedWrapper>
				</SideMenu>
				<MainContent sideMenuWidth={sideMenuWidth} className="hr-main-content">
					{children}
				</MainContent>
				<Footer sideMenuWidth={sideMenuWidth}>
					<SideMenuFooter />
				</Footer>
			</ContentWrapper>
		</Wrapper>
	);
};

export default SideMenuWrapper;
